import { useMemo } from 'react';
import { format, subDays } from 'date-fns';

const MOOD_COLORS = {
  1: '#94A3B8',
  2: '#4a7ba7',
  3: '#6fae8f',
  4: '#7b5ea7',
  5: '#f2b198',
};

const MOOD_LABEL = { 1: 'Difícil', 2: 'Baixo', 3: 'Neutro', 4: 'Bem', 5: 'Ótimo' };

export default function MoodChart({ logs, days = 30 }) {
  const data = useMemo(() => {
    const logMap = {};
    logs.forEach(l => {
      if (l.completed && l.mood_score) logMap[l.date] = l.mood_score;
    });
    return Array.from({ length: days }, (_, i) => {
      const date = subDays(new Date(), days - 1 - i);
      const key = format(date, 'yyyy-MM-dd');
      return { date: key, day: format(date, 'd'), mood: logMap[key] || null };
    });
  }, [logs, days]);

  const withMood = data.filter(d => d.mood);
  const avg = withMood.length
    ? (withMood.reduce((s, d) => s + d.mood, 0) / withMood.length).toFixed(1)
    : null;
  const dominant = withMood.length
    ? Object.entries(
        withMood.reduce((acc, d) => { acc[d.mood] = (acc[d.mood] || 0) + 1; return acc; }, {})
      ).sort((a, b) => b[1] - a[1])[0][0]
    : null;

  const W = 320, H = 100, padL = 8, padR = 8, padT = 8, padB = 20;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const barGap = chartW / days;
  const barW = Math.max(3, barGap - 2);

  return (
    <div>
      {withMood.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value" style={{ color: MOOD_COLORS[Math.round(avg)] }}>
              {parseFloat(avg).toFixed(1)}
            </div>
            <div className="stat-label">Humor médio</div>
          </div>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value" style={{ color: MOOD_COLORS[dominant], fontSize: 20 }}>
              {MOOD_LABEL[dominant]}
            </div>
            <div className="stat-label">Mais frequente</div>
          </div>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-value">{withMood.length}</div>
            <div className="stat-label">Registros</div>
          </div>
        </div>
      )}

      <div style={{ background: 'rgba(47,43,95,0.04)', borderRadius: 14, padding: '12px 8px 4px' }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 500, color: 'var(--muted)', textAlign: 'center', marginBottom: 8, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Últimos {days} dias
        </p>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
          {/* Linhas de referência */}
          {[1, 2, 3, 4, 5].map(level => {
            const y = padT + chartH - ((level - 1) / 4) * chartH;
            return (
              <line key={level}
                x1={padL} y1={y} x2={W - padR} y2={y}
                stroke="rgba(47,43,95,0.08)" strokeWidth={0.5}
              />
            );
          })}

          {/* Barras */}
          {data.map((d, i) => {
            const x = padL + i * barGap + (barGap - barW) / 2;
            if (!d.mood) {
              return (
                <rect key={d.date} x={x} y={padT + chartH - 3}
                  width={barW} height={3} rx={1.5}
                  fill="rgba(47,43,95,0.08)"
                />
              );
            }
            const barH = ((d.mood - 1) / 4) * chartH + 4;
            const y = padT + chartH - barH;
            return (
              <rect key={d.date} x={x} y={y}
                width={barW} height={barH}
                rx={Math.min(barW / 2, 3)}
                fill={MOOD_COLORS[d.mood]}
                opacity={0.75}
              />
            );
          })}

          {/* Labels de dia */}
          {data.map((d, i) => {
            if (i % 7 !== 0 && i !== days - 1) return null;
            const x = padL + i * barGap + barGap / 2;
            return (
              <text key={d.date} x={x} y={H - 4}
                fontSize={7} fill="rgba(46,46,46,0.4)" textAnchor="middle"
                fontFamily="DM Sans, sans-serif">
                {d.day}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5].map(m => (
          <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 300, color: 'var(--muted)' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: MOOD_COLORS[m] }} />
            {MOOD_LABEL[m]}
          </div>
        ))}
      </div>
    </div>
  );
}
